import React, { FC } from 'react';

import { Box, BoxProps, Flex, Group, Image, Stack, Text, VisuallyHidden } from '@mantine/core';

import { all as allComponents, ComponentType } from 'models';

import { ComponentIcon } from 'components/component-icons/ComponentIcon';
import { IKImage, IKImageProps } from 'components/ik-image/IKImage';
import { ReplusLogo } from 'components/logo/ReplusLogo';
import { Page } from 'components/page/Page';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';

import cx from './Hero.module.scss';

type Props = FC<
    BoxProps & {
        image?: {
            src: string;
            width: number;
            height: number;
        };
        imageFile?: IKImageProps;
        video?: {
            src: string;
            width: number;
            height: number;
        };
        customVideo?: React.ReactNode;
        children: React.ReactNode;
        actions?: React.ReactNode;
        showReplusLogo?: boolean;
    }
>;

export const Hero: Props = ({
    image = null,
    imageFile = null,
    video = null,
    customVideo = null,
    children,
    actions,
    showReplusLogo,
    ...props
}) => {
    // The loaded workspace document (a partner's or the main site's) decides; without one the logo shows.
    const { showReplusLogo: workspaceShowsReplusLogo } = useWorkspaceConfig();

    return (
        <Box className={cx.hero} {...props} data-hero>
            {image && <Image src={image.src} width={image.width} height={image.height} fetchPriority="high" />}
            {imageFile && <IKImage loading="eager" fetchPriority="high" {...imageFile} />}
            {video && (
                <video
                    src={video.src}
                    width={video.width}
                    height={video.height}
                    autoPlay
                    loop
                    muted
                    playsInline
                    // @ts-ignore this exists in new Chrome
                    fetchPriority="high"
                    style={{ pointerEvents: 'none' }}
                />
            )}
            {customVideo && customVideo}
            {actions && <Group className={cx.actions}>{actions}</Group>}
            <Page.WideContent style={{ display: 'block' }}>
                {showReplusLogo && workspaceShowsReplusLogo !== false && (
                    <Stack align="center" mb="md">
                        <ReplusLogo c="white" w={120} />
                        <VisuallyHidden>RE+Source PRO</VisuallyHidden>
                    </Stack>
                )}

                {children}
            </Page.WideContent>
        </Box>
    );
};

export const HeroComponentIcon: FC<{ type: ComponentType; selected?: boolean }> = ({ type, selected }) => {
    return (
        <Box className={cx.componentIcon} data-selected={selected}>
            <Box className={cx.icon}>
                <ComponentIcon type={type} />
            </Box>
            <Text className={cx.title}>{allComponents[type as ComponentType].plural}</Text>
        </Box>
    );
};
